import { ClientError } from "#lib/errors"
import { DashboardSDK } from "#sdk/dashboard"
import { ExpenseCategoriesSDK } from "#sdk/expense_categories"
import { ExpensesSDK } from "#sdk/expenses"
import { PartnersSDK } from "#sdk/partners"
import { Account } from "@home-finance-manager/shared/types/accounts"
import { Partner } from "@home-finance-manager/shared/types/partners"
import { setOnCurrentAccountChange } from "./account-callback"
import { BaseSDK } from "./base-sdk"

export class AccountsSDK extends BaseSDK {
  #partners!: PartnersSDK
  #expenseCategories!: ExpenseCategoriesSDK
  #expenses!: ExpensesSDK
  #dashboard!: DashboardSDK
  #currentAccountId: number | null = null

  constructor(
    baseUrl: string,
    getAuthHeaders: () => { Authorization: string; "Content-Type": string }
  ) {
    super(baseUrl, getAuthHeaders)
    // Set up the callback to reset instances when account changes
    setOnCurrentAccountChange((account) => {
      if (!account) return
      // Reset all instances when account changes
      this.#currentAccountId = account.id
      this.#partners = undefined as any
      this.#expenseCategories = undefined as any
      this.#expenses = undefined as any
      this.#dashboard = undefined as any
    })
  }

  get partners() {
    const rawCurrentAccount = localStorage.getItem("current_account")
    const currentAccount = rawCurrentAccount ? JSON.parse(rawCurrentAccount) : null
    const accountId = currentAccount?.id

    // Reset if account changed or not initialized
    if (!this.#partners || this.#currentAccountId !== accountId) {
      if (!accountId) {
        throw new ClientError("No account selected", "NO_ACCOUNT", 400)
      }
      this.#currentAccountId = accountId
      const baseUrl = `${this.baseUrl}/accounts/${accountId}`
      this.#partners = new PartnersSDK(baseUrl, this.getAuthHeaders)
      this.#expenseCategories = new ExpenseCategoriesSDK(baseUrl, this.getAuthHeaders)
      this.#expenses = new ExpensesSDK(baseUrl, this.getAuthHeaders)
      this.#dashboard = new DashboardSDK(baseUrl, this.getAuthHeaders)
    }

    return this.#partners
  }

  get expenseCategories() {
    const rawCurrentAccount = localStorage.getItem("current_account")
    const currentAccount = rawCurrentAccount ? JSON.parse(rawCurrentAccount) : null
    const accountId = currentAccount?.id

    // Reset if account changed or not initialized
    if (!this.#expenseCategories || this.#currentAccountId !== accountId) {
      if (!accountId) {
        throw new ClientError("No account selected", "NO_ACCOUNT", 400)
      }
      this.#currentAccountId = accountId
      const baseUrl = `${this.baseUrl}/accounts/${accountId}`
      this.#partners = new PartnersSDK(baseUrl, this.getAuthHeaders)
      this.#expenseCategories = new ExpenseCategoriesSDK(baseUrl, this.getAuthHeaders)
      this.#expenses = new ExpensesSDK(baseUrl, this.getAuthHeaders)
      this.#dashboard = new DashboardSDK(baseUrl, this.getAuthHeaders)
    }

    return this.#expenseCategories
  }

  get expenses() {
    const rawCurrentAccount = localStorage.getItem("current_account")
    const currentAccount = rawCurrentAccount ? JSON.parse(rawCurrentAccount) : null
    const accountId = currentAccount?.id

    // Reset if account changed or not initialized
    if (!this.#expenses || this.#currentAccountId !== accountId) {
      if (!accountId) {
        throw new ClientError("No account selected", "NO_ACCOUNT", 400)
      }
      this.#currentAccountId = accountId
      const baseUrl = `${this.baseUrl}/accounts/${accountId}`
      this.#partners = new PartnersSDK(baseUrl, this.getAuthHeaders)
      this.#expenseCategories = new ExpenseCategoriesSDK(baseUrl, this.getAuthHeaders)
      this.#expenses = new ExpensesSDK(baseUrl, this.getAuthHeaders)
      this.#dashboard = new DashboardSDK(baseUrl, this.getAuthHeaders)
    }

    return this.#expenses
  }

  get dashboard() {
    const rawCurrentAccount = localStorage.getItem("current_account")
    const currentAccount = rawCurrentAccount ? JSON.parse(rawCurrentAccount) : null
    const accountId = currentAccount?.id

    // Reset if account changed or not initialized
    if (!this.#dashboard || this.#currentAccountId !== accountId) {
      if (!accountId) {
        throw new ClientError("No account selected", "NO_ACCOUNT", 400)
      }
      this.#currentAccountId = accountId
      const baseUrl = `${this.baseUrl}/accounts/${accountId}`
      this.#partners = new PartnersSDK(baseUrl, this.getAuthHeaders)
      this.#expenseCategories = new ExpenseCategoriesSDK(baseUrl, this.getAuthHeaders)
      this.#expenses = new ExpensesSDK(baseUrl, this.getAuthHeaders)
      this.#dashboard = new DashboardSDK(baseUrl, this.getAuthHeaders)
    }

    return this.#dashboard
  }

  /**
   * Get all accounts for the authenticated user
   * @returns Array of accounts
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getAccounts(): Promise<Account[]> {
    const response = await this.fetchWithErrorHandling<{ data: Account[] }>(
      `${this.baseUrl}/accounts`,
      {
        headers: this.getAuthHeaders(),
      }
    )
    return this.extractData<Account[]>(response) || []
  }

  /**
   * Get a specific account by ID
   * @param id - The account ID
   * @returns The account
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getAccount(id: number): Promise<Account> {
    const response = await this.fetchWithErrorHandling<{ data: Account }>(
      `${this.baseUrl}/accounts/${id}`,
      {
        headers: this.getAuthHeaders(),
      }
    )
    return this.extractData<Account>(response)
  }

  /**
   * Create a new account
   * @param name - The account name
   * @returns The created account
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async createAccount(name: string): Promise<Account> {
    const response = await this.fetchWithErrorHandling<{ data: Account }>(
      `${this.baseUrl}/accounts`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ name }),
      }
    )
    return this.extractData<Account>(response)
  }

  /**
   * Update an account
   * @param id - The account ID
   * @param name - The new account name
   * @returns The updated account
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async updateAccount(id: number, name: string): Promise<Account> {
    const response = await this.fetchWithErrorHandling<{ data: Account }>(
      `${this.baseUrl}/accounts/${id}`,
      {
        method: "PUT",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ name }),
      }
    )
    return this.extractData<Account>(response)
  }

  /**
   * Set an account as default
   * @param id - The account ID
   * @returns Success message
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async setDefaultAccount(id: number): Promise<{ message: string }> {
    return this.fetchWithErrorHandling<{ message: string }>(
      `${this.baseUrl}/accounts/${id}/set-default`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
      }
    )
  }

  /**
   * Invite a user to an account
   * @param accountId - The account ID
   * @param email - The user's email
   * @param role - The user's role (default: 'member')
   * @returns Success message
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async inviteUser(
    accountId: number,
    email: string,
    role = "member"
  ): Promise<{ message: string }> {
    return this.fetchWithErrorHandling<{ message: string }>(
      `${this.baseUrl}/accounts/${accountId}/invite`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ email, role }),
      }
    )
  }

  /**
   * Get all partners for a specific account
   * @param accountId - The account ID
   * @returns Array of partners
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getAccountPartners(accountId: number): Promise<Partner[]> {
    const response = await this.fetchWithErrorHandling<{ data: Partner[] }>(
      `${this.baseUrl}/accounts/${accountId}/partners`,
      {
        headers: this.getAuthHeaders(),
      }
    )
    return this.extractData<Partner[]>(response) || []
  }

  /**
   * Create a new partner for an account
   * @param accountId - The account ID
   * @param partnerData - The partner data
   * @returns The created partner
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async createPartner(
    accountId: number,
    partnerData: {
      firstName: string
      lastName: string
      email: string
      annualGrossSalary: number
      annualNetSalary: number
    }
  ): Promise<Partner> {
    const response = await this.fetchWithErrorHandling<{ data: Partner }>(
      `${this.baseUrl}/accounts/${accountId}/partners`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(partnerData),
      }
    )
    return this.extractData<Partner>(response)
  }

  /**
   * Get all members of an account
   * @param accountId - The account ID
   * @returns List of account members with their roles
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getMembers(accountId: number): Promise<
    Array<{
      id: number
      email: string
      fullName: string | null
      role: string
      owner: boolean
      isDefault: boolean
    }>
  > {
    const response = await this.fetchWithErrorHandling<{
      data: Array<{
        id: number
        email: string
        fullName: string | null
        role: string
        owner: boolean
        isDefault: boolean
      }>
    }>(`${this.baseUrl}/accounts/${accountId}/members`, {
      method: "GET",
      headers: this.getAuthHeaders(),
    })
    return this.extractData(response)
  }

  /**
   * Remove a member from an account
   * @param accountId - The account ID
   * @param userId - The user ID to remove
   * @returns Success message
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async removeMember(accountId: number, userId: number): Promise<{ message: string }> {
    return this.fetchWithErrorHandling(`${this.baseUrl}/accounts/${accountId}/members/${userId}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    })
  }

  /**
   * Update a member's role in an account
   * @param accountId - The account ID
   * @param userId - The user ID
   * @param role - The new role (owner, admin, member)
   * @returns Success message
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async updateMemberRole(
    accountId: number,
    userId: number,
    role: string
  ): Promise<{ message: string }> {
    return this.fetchWithErrorHandling(
      `${this.baseUrl}/accounts/${accountId}/members/${userId}/role`,
      {
        method: "PUT",
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ role }),
      }
    )
  }
}
